import React, { useState, useContext, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import selectors from '@state/selectors';
import {IProps, IState} from './types';
import * as S from './styles';
import { RectangleIcon } from '@assets/icons/rectangle';
import { StartIcon } from '@assets/icons/startIcon';
import { Context } from "routes/private/Editor/Context";
import { findElementById, findOEM } from '@utils/editor';
import { IElement, IShapeCircleAttributes, IShapeCommonAttributes, IShapeEllipseAttributes, IShapeLineAttributes, IShapeRectangleAttributes, IShapeRhombusAttributes, IShapeStarAttributes, IShapeTriangleAttributes, IShapeTypes, ITextAttributes } from '@routes/private/Editor/types';
import DragElement from '../DragElement';
import { CircleIcon } from '@assets/icons/circleIcon';
import { TriangleIcon } from '@assets/icons/triangleIcon';
import { RhombusIcon } from '@assets/icons/rhombusIcon';
import { EllipseIcon } from '@assets/icons/ellipseIcon';
import { LineIcon, VerticalLineIcon } from '@assets/icons/lineIcon';

const uuid = require('react-uuid');

const NewShapePicker:React.FC<IProps> = (props) => {
  let context = useContext(Context);
  const dsConfig = useSelector(selectors.dsConfig.getDSConfig);

  useEffect(() => {
    const draggingData = context.editorStatus.draggingData;
    if (draggingData?.type === "Shape") {
      const shapeType: any = draggingData.id;
      handleAddDropShape(shapeType, draggingData.containerId, draggingData.data, draggingData.coorsPercentage, draggingData.elementCoorsPercentage);
    }
  }, [context.editorStatus.draggingData]);
  
  const handleAddShapeManager = (shape: IShapeTypes, data?:any) => {

    let ids: string[] = [];
    if (context.data[0].type === "Artboards") {
      if (context.editorStatus.selectedArtboardIds && context.editorStatus.selectedArtboardIds.length > 0) {
        context.editorStatus.selectedArtboardIds.map((id: string) => {
          ids = [...ids, id];
        });
      } else if (context.data[0]?.children && context.data[0]?.children.length > 0) {
        context.data[0].children.map((child: IElement) => {
          ids = [...ids, child.id];
        });
      }
      handleAddShape(ids, shape,data);
    } else {
      handleAddShape([context.data[0].id], shape,data);
    }

  }

  const getShapeOffset = (shape:IShapeTypes, data?:any) : any =>
  {
    if (shape === "Rectangle")
      return {x: 100, y: 50}
    else if (shape==="Line")
    {
      if (data && data.rotation)
        return {x: 0, y: 75}
      else
        return {x: 100, y: 0}
    }
    else if (shape === "Triangle")
      return {x: 0, y: -15}
    else if (shape === "Star")
      return {x: 0, y: -5}
    else 
      return {x: 0, y: 0}
  }

  const handleAddShape = (ids: string[] , shape: IShapeTypes, data?:any,coors?: {x: number, y: number}, elementCoors?: {x: number, y: number}) => {

    let newShapesElements: IElement[] = [];
    ids.map(id => {
      let parentElement: IElement | undefined = findElementById(context.data, id);
      if (parentElement === undefined) parentElement = context.data[0];
      let shapeOffset = getShapeOffset(shape,data);
      let coorX = parentElement.attributes.x - shapeOffset.x;
      let coorY = parentElement.attributes.y - shapeOffset.y;
      let shapeWidth = shape === "Line" ? ((data && data.rotation) ? 150 : 200) : shape === "Ellipse" || shape === "Rectangle" ? 200 : shape==="Triangle" ? 120 : 100;
      let shapeHeight = shape === "Line" && data?.stroke ? data.stroke : shape === "Triangle" ? 120 : 100;
      if (coors && elementCoors) {
        const xOffset = shape === "Line" && data?.rotation ? shapeOffset.x * 2 : shapeWidth;
        const yOffset = shape === "Line" && data?.rotation ? shapeOffset.y * 2 : shapeHeight;
        coorX += coors.x * parentElement.attributes.width - elementCoors.x * xOffset + xOffset / 2;
        coorY += coors.y * parentElement.attributes.height - elementCoors.y * yOffset + yOffset / 2;
      } else {
        coorX += parentElement.attributes.width / 2;
        coorY += parentElement.attributes.height / 2;
      }
      let newAttributes: IShapeCommonAttributes | IShapeCircleAttributes | IShapeEllipseAttributes | IShapeLineAttributes | IShapeRectangleAttributes | IShapeRhombusAttributes | IShapeStarAttributes | IShapeTriangleAttributes;
    
      newAttributes = {
        x: coorX,
        y: coorY,
        width: shapeWidth,
        height: shapeHeight,
        hidden: false,         
        shapeType: shape,
        backgroundColor:"#808080",
        opacity:1,
        numPoints: shape==="Star" ? 5 : undefined,
        rotation: data && data.rotation?data.rotation:0,
        lineCap: data && data.lineCap,
        borderColor: shape==="Line" ? "gray" : undefined,
        borderWidth: shape==="Line" ? (data && data.stroke ? data.stroke : 2) : undefined,
      };

      const newShapeElement: IElement = {
        id: uuid(),
        name: shape,
        type: "Shape",
        attributes: newAttributes
      };
      newShapesElements = [...newShapesElements, newShapeElement];
      
    })
    context.onCommandChange("addElement", undefined, ids, newShapesElements);
  }

  const handleAddDropShape = (shape: IShapeTypes, id?: string, data?:any, coors?: {x: number, y: number}, elementCoors?: {x: number, y: number}) => {

    const selectedArtboards: string[] = context.editorStatus.selectedArtboardIds ? context.editorStatus.selectedArtboardIds : [];
    const idIndex = selectedArtboards.findIndex((artBoardId: string) => (artBoardId === id));
    if (id) {
      if (idIndex >= 0) {
        if (selectedArtboards.length === 1) handleAddShape([id], shape,data, coors, elementCoors);
        else handleAddShapeManager(shape,data);
      } else {
        context.onSelectedArtboardChange(id);
        handleAddShape([id], shape,data, coors, elementCoors);
      }
    } else {
      handleAddShape([context.data[0].id], shape,data, coors, elementCoors);
    }
  }

  return (
    <S.Root>
      <S.HelpText>
        Click shape to add to page
      </S.HelpText>
      <S.ShapesContainer>
        <S.ShapeContainer  onClick={()=>handleAddShapeManager("Rectangle")}>
          <DragElement id="Rectangle" type="Shape">
            <RectangleIcon ></RectangleIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Circle")}>
          <DragElement id="Circle" type="Shape">
            <CircleIcon ></CircleIcon>      
          </DragElement>
        </S.ShapeContainer>        
        <S.ShapeContainer  onClick={()=>handleAddShapeManager("Star")}>
          <DragElement id="Star" type="Shape">
            <StartIcon></StartIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Triangle")}>
          <DragElement id="Triangle" type="Shape"> 
            <TriangleIcon></TriangleIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Rhombus")}> 
          <DragElement id="Rhombus" type="Shape">
            <RhombusIcon></RhombusIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Ellipse")}>
          <DragElement id="Ellipse" type="Shape">
            <EllipseIcon></EllipseIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Line")}>
          <DragElement id="Line" type="Shape">
            <LineIcon></LineIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Line",{stroke:3})}>
          <DragElement id="Line" data={{stroke:3}} type="Shape">
            <LineIcon strokeWidth={3}></LineIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Line",{stroke:10})}>
          <DragElement id="Line" data={{stroke:10}} type="Shape">
            <LineIcon strokeWidth={10}></LineIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Line",{lineCap:"round"})}>
          <DragElement id="Line" data={{lineCap:"round"}} type="Shape">
            <LineIcon rounded={true}></LineIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Line",{stroke:3,lineCap:"round"})}>
          <DragElement id="Line" data={{stroke:3, lineCap:"round"}} type="Shape">
            <LineIcon strokeWidth={3} rounded={true}></LineIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeContainer onClick={()=>handleAddShapeManager("Line",{stroke:10,lineCap:"round"})}>
          <DragElement id="Line" data={{stroke:10, lineCap:"round"}} type="Shape">
            <LineIcon strokeWidth={10} rounded={true}></LineIcon>
          </DragElement>
        </S.ShapeContainer>
        <S.ShapeVerticalContainer onClick={()=>handleAddShapeManager("Line",{rotation:90})}>
          <DragElement id="Line" data={{rotation:90}} type="Shape">
            <VerticalLineIcon></VerticalLineIcon>
          </DragElement>
        </S.ShapeVerticalContainer>
        <S.ShapeVerticalContainer onClick={()=>handleAddShapeManager("Line",{stroke:3, rotation:90})}>
          <DragElement id="Line" data={{stroke:3,rotation:90}} type="Shape">
            <VerticalLineIcon strokeWidth={3}></VerticalLineIcon>
            </DragElement>
        </S.ShapeVerticalContainer>
        <S.ShapeVerticalContainer onClick={()=>handleAddShapeManager("Line",{stroke:10, rotation:90})}>
          <DragElement id="Line" data={{stroke:10,rotation:90}} type="Shape">
            <VerticalLineIcon strokeWidth={10}></VerticalLineIcon>
          </DragElement>
        </S.ShapeVerticalContainer>
        <S.ShapeVerticalContainer  onClick={()=>handleAddShapeManager("Line",{rotation:90, lineCap:"round"})}>
          <DragElement id="Line" data={{rotation:90, lineCap:"round"}} type="Shape">
            <VerticalLineIcon rounded={true}></VerticalLineIcon>
          </DragElement>
        </S.ShapeVerticalContainer>
        <S.ShapeVerticalContainer  onClick={()=>handleAddShapeManager("Line",{stroke:3, rotation:90, lineCap:"round"})}>
          <DragElement id="Line" data={{stroke:3, rotation:90, lineCap:"round"}} type="Shape">
            <VerticalLineIcon strokeWidth={3} rounded={true}></VerticalLineIcon>
          </DragElement>
        </S.ShapeVerticalContainer>
        <S.ShapeVerticalContainer  onClick={()=>handleAddShapeManager("Line",{stroke:10, rotation:90, lineCap:"round"})}>
          <DragElement id="Line" data={{stroke:10, rotation:90, lineCap:"round"}} type="Shape">
            <VerticalLineIcon strokeWidth={10} rounded={true}></VerticalLineIcon>
          </DragElement>
        </S.ShapeVerticalContainer>
      </S.ShapesContainer>
    </S.Root>
  );
}

export default NewShapePicker;